import { useState } from 'react'
import { Check, ChevronRight, Lock, Circle, Clock, Workflow } from 'lucide-react'
import { cn } from '@/lib/utils'
import { STATUS_META } from '@/lib/journey'
import { assigneeInitials } from '@/lib/assignedTo'
import { AssigneeSelect } from '@/components/procedure/AssigneeSelect'
import { FORM_CATEGORY_COLORS, FORM_CATEGORY_LABELS } from '@/types/cgt'
import type {
  ProcedureAssignee,
  FormCategory,
  ProcedureJourneyRepeat,
  ProcedureJourneyStep,
  ProcedureStepStatus,
} from '@/types/cgt'
import {
  bucketRepeats,
  isRepeatingStep,
  journeyProgress,
  repeatLabel,
  stepProgress,
  stepUnits,
  type RepeatBuckets,
} from '@/lib/repeats'
import { lagNote, type LagTone } from '@/lib/lag'

/**
 * What a repeating step's performances can be filtered down to: everything, or
 * one of the three buckets the spec lists — what is done, what is open, what is
 * still to come.
 *
 * A table rather than four hand-written controls so a filter cannot end up with
 * a label that disagrees with the list under it — the same reason the actions
 * checklist maps over a list instead of repeating itself.
 *
 * "All" leads because it is the unfiltered view: the buckets after it read as
 * narrowings of it rather than as three separate places to look. It is not the
 * *default* though — see `defaultBucketFor`.
 */
type BucketFilter = keyof RepeatBuckets | 'all'

const REPEAT_BUCKETS = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Completed' },
  { key: 'inProgress', label: 'In progress' },
  { key: 'upcoming', label: 'Upcoming' },
] as const satisfies ReadonlyArray<{ key: BucketFilter; label: string }>

/** Whichever bucket has the most immediate work: in progress, else upcoming, else completed. */
function defaultBucketFor(buckets: RepeatBuckets): BucketFilter {
  if (buckets.inProgress.length > 0) return 'inProgress'
  if (buckets.upcoming.length > 0) return 'upcoming'
  return 'completed'
}

/**
 * The performances one filter shows. "All" is every performance in run order
 * rather than the three buckets concatenated, so the numbering reads 1, 2, 3
 * down the list instead of jumping about as rows change status.
 */
function repeatsForFilter(
  filter: BucketFilter,
  buckets: RepeatBuckets,
  all: ProcedureJourneyRepeat[],
): ProcedureJourneyRepeat[] {
  return filter === 'all' ? all : buckets[filter]
}

const LAG_TONE_CLASS: Record<LagTone, string> = {
  waiting: 'text-slate-500',
  due: 'text-amber-600',
  overdue: 'text-rose-600 font-medium',
}

function StatusDot({ status }: { status: ProcedureStepStatus }) {
  const base = 'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border'
  switch (status) {
    case 'completed':
      return (
        <span className={cn(base, 'border-emerald-500 bg-emerald-500 text-white')}>
          <Check className='h-3 w-3' strokeWidth={3} />
        </span>
      )
    case 'in_progress':
      return (
        <span className={cn(base, 'border-amber-500 text-amber-600')}>
          <Clock className='h-3 w-3' />
        </span>
      )
    case 'locked':
      return (
        <span className={cn(base, 'border-slate-200 text-slate-300')}>
          <Lock className='h-3 w-3' />
        </span>
      )
    default:
      return (
        <span className={cn(base, 'border-slate-300 text-slate-300')}>
          <Circle className='h-2 w-2 fill-current' />
        </span>
      )
  }
}

interface Props {
  procedureName: string
  steps: ProcedureJourneyStep[]
  selectedStepId: string | null
  /**
   * Opens a step. For a REPEATING step the chosen repeat is passed too, because
   * each repeat is its own submission and "open this step" is otherwise
   * ambiguous. Omitted for a step that runs once — callers that predate repeats
   * keep working unchanged.
   */
  onSelect: (step: ProcedureJourneyStep, repeat?: ProcedureJourneyRepeat) => void
  /** The repeat currently open, when the selected step repeats. */
  selectedRepeatIndex?: number | null
  /** Form type (category) per step id — tints the step-row background. */
  categoryByStepId?: Record<string, FormCategory>
  /** Step IDs whose form contains anchored fields but whose category is not 'anchored'. */
  anchoredFieldStepIds?: Set<string>
  /** Step IDs that are ad-hoc forms (added on the fly, not part of the procedure template). */
  adHocStepIds?: Set<string>
  /** Opens a read-only popup of the procedure's flow diagram. Omit to hide the button. */
  onViewFlow?: () => void
  /** Whether the viewer may change Assign To (procedures.assign). */
  canAssign?: boolean
  /** Roster for the inline per-form assign pickers. */
  assignableUsers?: ProcedureAssignee[]
  /** Fired when a row's inline picker changes (null = unassign). */
  onAssignStep?: (step: ProcedureJourneyStep, assigneeId: string | null) => void
  /** Step currently saving an assignment — disables that row's picker. */
  assignSavingStepId?: string | null
  /**
   * Fired when ONE repeat's picker changes (null = unassign, which drops the
   * repeat back to whoever holds the step).
   */
  onAssignRepeat?: (
    step: ProcedureJourneyStep,
    repeat: ProcedureJourneyRepeat,
    assigneeId: string | null,
  ) => void
  /** `stepId:repeatIndex` currently saving — disables just that row's picker. */
  assignSavingRepeatKey?: string | null
}

/**
 * Left-rail list of the procedure's forms for one patient. Selecting a step
 * surfaces that form in the main content area (the parent owns selection).
 */
export function ProcedureFormSidebar({
  procedureName,
  steps,
  selectedStepId,
  onSelect,
  selectedRepeatIndex,
  categoryByStepId,
  anchoredFieldStepIds,
  adHocStepIds,
  onViewFlow,
  canAssign,
  assignableUsers,
  onAssignStep,
  assignSavingStepId,
  onAssignRepeat,
  assignSavingRepeatKey,
}: Props) {
  // Counts each repeat of a repeating step separately, so a 3x step contributes
  // 3 to the denominator. A journey with no repeating steps produces exactly the
  // numbers this used to.
  const { completed: completedCount, total: totalCount, pct } = journeyProgress(steps)

  /*
   * Which repeating steps are open. Local rather than lifted: which groups a
   * user has unfolded is a property of looking at the rail, not of the case,
   * and persisting it would mean restoring a half-expanded rail on every
   * navigation.
   *
   * Seeded with the step whose repeat is currently open, so arriving on a
   * repeat's form by URL does not show it collapsed inside a group.
   */
  const [expandedStepIds, setExpandedStepIds] = useState<Set<string>>(
    () => new Set(selectedRepeatIndex != null && selectedStepId ? [selectedStepId] : []),
  )
  const toggleExpanded = (stepId: string) =>
    setExpandedStepIds(prev => {
      const next = new Set(prev)
      if (!next.delete(stepId)) next.add(stepId)
      return next
    })

  /**
   * Which filter (All / Completed / In progress / Upcoming) an expanded step is
   * showing. Per step, same reasoning as `expandedStepIds` — a rail-viewing
   * preference, not case state. Unset until the viewer picks one, at which
   * point the step falls back to whichever bucket has the most immediate
   * work (see `defaultBucketFor` above).
   */
  const [activeBucketByStep, setActiveBucketByStep] = useState<
    Record<string, BucketFilter>
  >({})

  return (
    <div className='flex h-full flex-col'>
      <div className='border-b px-3 py-2'>
        <div className='flex items-start justify-between gap-2'>
          <p className='truncate text-sm font-semibold' title={procedureName}>
            {procedureName}
          </p>
          {onViewFlow && (
            <button
              type='button'
              onClick={onViewFlow}
              title='View procedure flow'
              className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md border bg-blue-50 text-blue-600 hover:bg-blue-100'
            >
              <Workflow className='h-3.5 w-3.5' />
            </button>
          )}
        </div>
        <p className='mt-0.5 text-xs text-muted-foreground'>
          {completedCount} of {totalCount} completed
        </p>
        <div className='mt-1.5 flex items-center gap-2'>
          <div className='h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200'>
            <div
              className='h-full rounded-full bg-emerald-500 transition-all duration-500'
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className='text-[11px] font-medium tabular-nums text-muted-foreground'>
            {pct}%
          </span>
        </div>
      </div>

      <nav className='flex-1 overflow-y-auto p-2'>
        {steps.length === 0 ? (
          <p className='px-2 py-6 text-center text-xs text-muted-foreground'>
            This procedure has no forms yet.
          </p>
        ) : (
          <ul className='space-y-1'>
            {steps.map(step => {
              const meta = STATUS_META[step.status]
              const selected = step.stepId === selectedStepId
              const category = categoryByStepId?.[step.stepId]
              const hex = category ? FORM_CATEGORY_COLORS[category] : undefined
              const repeating = isRepeatingStep(step)
              const repeats = stepUnits(step)
              const progress = stepProgress(step)
              const buckets = bucketRepeats(step)
              const expanded = expandedStepIds.has(step.stepId)
              // A repeating step's row is a DISCLOSURE, not a destination:
              // there is no single submission behind it to open, so clicking it
              // reveals the performances instead of guessing one.
              const activate = () =>
                repeating ? toggleExpanded(step.stepId) : onSelect(step)
              return (
                <li key={step.stepId}>
                  {/* A div-with-role rather than a <button>: the Assign To chip
                      inside needs to be a real (nested) button. */}
                  <div
                    role='button'
                    tabIndex={0}
                    aria-expanded={repeating ? expanded : undefined}
                    onClick={activate}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        activate()
                      }
                    }}
                    style={
                      hex
                        ? { backgroundColor: `${hex}${selected ? '33' : '1f'}` } // ~20% / ~12% alpha
                        : undefined
                    }
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                      selected && 'ring-1 ring-blue-300',
                      !hex && (selected ? 'bg-blue-50' : 'hover:bg-muted'),
                    )}
                  >
                    {anchoredFieldStepIds?.has(step.stepId) && (
                      <span
                        className='h-2 w-2 shrink-0 rounded-full bg-red-500'
                        title='Contains anchored fields'
                      />
                    )}
                    {repeating && (
                      <ChevronRight
                        className={cn(
                          'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                          expanded && 'rotate-90',
                        )}
                      />
                    )}
                    <StatusDot status={step.status} />
                    <span className='min-w-0 flex-1'>
                      <span
                        className={cn(
                          'flex items-center gap-1.5 truncate text-sm',
                          selected ? 'font-semibold text-blue-900' : 'font-medium',
                        )}
                      >
                        <span className='truncate'>{step.formName}</span>
                        {/* "3 / 10" — done of repeats, as the spec words it.
                            A fraction rather than a multiplier, because the
                            number a user wants off this row is how far through
                            they are, not how many there are in total. */}
                        {repeating && (
                          <span
                            title={`${progress.completed} of ${progress.total} performed`}
                            className='shrink-0 rounded bg-slate-200 px-1 py-px text-[10px] font-semibold tabular-nums text-slate-700'
                          >
                            {progress.completed} / {progress.total}
                          </span>
                        )}
                      </span>
                      {adHocStepIds?.has(step.stepId) && (
                        <span
                          className='inline-block rounded px-1 py-px text-[10px] font-medium text-white'
                          style={{ backgroundColor: category ? FORM_CATEGORY_COLORS[category] : '#94a3b8' }}
                        >
                          {category ? FORM_CATEGORY_LABELS[category] : 'Ad-hoc'}
                        </span>
                      )}
                      <span className={cn('text-[11px]', meta.text)}>{meta.label}</span>
                      {(() => {
                        const note = lagNote(step)
                        return note ? (
                          <span className={cn('block text-[11px]', LAG_TONE_CLASS[note.tone])}>
                            {note.text}
                          </span>
                        ) : null
                      })()}
                    </span>
                    {canAssign && onAssignStep ? (
                      <span className='shrink-0' onClick={e => e.stopPropagation()}>
                        <AssigneeSelect
                          variant='avatar'
                          users={assignableUsers ?? []}
                          value={step.assignee ?? null}
                          disabled={assignSavingStepId === step.stepId}
                          onChange={assigneeId => onAssignStep(step, assigneeId)}
                        />
                      </span>
                    ) : step.assignee ? (
                      <span
                        title={`Assigned to ${step.assignee.name}`}
                        className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white'
                      >
                        {assigneeInitials(step.assignee.name)}
                      </span>
                    ) : (
                      <span className='w-7 shrink-0 text-center text-[10px] text-slate-300'>—</span>
                    )}
                  </div>

                  {/* The performances themselves. Anchored under the parent by a
                      rule, the same device the actions checklist uses for its
                      nested options, so the grouping reads at a glance. */}
                  {repeating && expanded && (
                    <div className='ml-2 mt-1 space-y-2 border-l border-border/70 pl-1.5'>
                      {/* This step's own progress, separate from the run's.
                          A repeating step is a little journey of its own and
                          the mockup gives it its own bar. */}
                      <div className='px-1'>
                        <div className='h-1 overflow-hidden rounded-full bg-slate-200'>
                          <div
                            className='h-full rounded-full bg-emerald-500 transition-all duration-500'
                            style={{ width: `${progress.pct}%` }}
                          />
                        </div>
                      </div>

                      {/*
                        A filter, not stacked sections — a step with many
                        repeats otherwise means scrolling past everything
                        already done to find what's next. "All" is there for
                        the other reading of the same rail: the whole run in
                        order, which is what you want when checking a step over
                        rather than working the next thing in it. Small pills
                        rather than a boxed tab bar: this rail is narrow (nested
                        a level in under the step row), and the pill is the same
                        device the "3 / 10" counter chip above already uses,
                        so it reads as part of this sidebar rather than a
                        dropped-in widget.
                      */}
                      <div className='flex flex-wrap gap-1 px-1'>
                        {REPEAT_BUCKETS.map(({ key, label }) => {
                          const count = repeatsForFilter(key, buckets, repeats).length
                          const active = (activeBucketByStep[step.stepId] ?? defaultBucketFor(buckets)) === key
                          return (
                            <button
                              key={key}
                              type='button'
                              disabled={count === 0}
                              onClick={() =>
                                setActiveBucketByStep(prev => ({ ...prev, [step.stepId]: key }))
                              }
                              className={cn(
                                'rounded-full px-2 py-0.5 text-[10px] font-medium tabular-nums transition-colors',
                                count === 0
                                  ? 'cursor-default text-slate-300'
                                  : active
                                    ? 'bg-slate-800 text-white'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                              )}
                            >
                              {label} ({count})
                            </button>
                          )
                        })}
                      </div>

                      {(() => {
                        const activeBucket = activeBucketByStep[step.stepId] ?? defaultBucketFor(buckets)
                        const items = repeatsForFilter(activeBucket, buckets, repeats)
                        if (items.length === 0) {
                          return (
                            <p className='px-1 py-3 text-center text-[11px] text-muted-foreground'>
                              Nothing here yet.
                            </p>
                          )
                        }
                        return (
                          <ul className='space-y-1'>
                            {items.map(repeat => {
                              const repeatMeta = STATUS_META[repeat.status]
                              const repeatSelected =
                                selected && selectedRepeatIndex === repeat.repeatIndex
                              // Repeats are not sequenced: the only thing
                              // that can lock one is its own step (an
                              // unmet predecessor, a lag window), so a
                              // locked repeat just shows the step's status.
                              const repeatLocked = repeat.status === 'locked'
                              const openRepeat = () => {
                                if (repeatLocked) return
                                onSelect(step, repeat)
                              }
                              return (
                                <li key={repeat.repeatIndex}>
                                  {/* div-with-role, not <button>: the assignee
                                      picker inside is itself a button, and
                                      nesting one inside another is invalid.
                                      Same reason the step row above is one. */}
                                  <div
                                    role='button'
                                    tabIndex={repeatLocked ? -1 : 0}
                                    aria-disabled={repeatLocked}
                                    onClick={openRepeat}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        openRepeat()
                                      }
                                    }}
                                    // Each performance carries its form's
                                    // colour, at a lighter weight than the
                                    // step row above it so the two read as
                                    // parent and child of one form rather
                                    // than as two forms. A locked row is left
                                    // untinted: it is not available, and
                                    // colouring it the same as a workable one
                                    // says it is.
                                    style={
                                      hex && !repeatLocked
                                        ? {
                                            backgroundColor: `${hex}${repeatSelected ? '2b' : '14'}`,
                                          }
                                        : undefined
                                    }
                                    className={cn(
                                      // Same metrics as the step row above it.
                                      // A performance is a form somebody opens
                                      // and works, so it gets a form row, not a
                                      // shrunken index entry — the nesting rule
                                      // and the lighter tint already say which
                                      // is the parent, and a smaller hit target
                                      // buys nothing for saying it twice.
                                      'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                                      repeatLocked
                                        ? 'cursor-not-allowed opacity-60'
                                        : 'cursor-pointer',
                                      repeatSelected
                                        ? 'ring-1 ring-blue-300'
                                        : !repeatLocked && !hex && 'hover:bg-muted',
                                      repeatSelected && !hex && 'bg-blue-50',
                                    )}
                                  >
                                    <StatusDot status={repeat.status} />
                                    <span className='min-w-0 flex-1'>
                                      <span
                                        className={cn(
                                          'block truncate text-sm',
                                          repeatSelected
                                            ? 'font-semibold text-blue-900'
                                            : 'font-medium',
                                        )}
                                      >
                                        {repeatLabel(
                                          step.formName,
                                          repeat.repeatIndex,
                                          repeats.length,
                                        )}
                                      </span>
                                      <span className={cn('block truncate text-[11px]', repeatMeta.text)}>
                                        {repeatMeta.label}
                                      </span>
                                    </span>
                                    {/* Each performance is somebody's job, so
                                        each carries its own assignee. Falls
                                        back to the step's, so a step assigned
                                        as a whole already shows that person
                                        on every row. */}
                                    {canAssign && onAssignRepeat ? (
                                      <span
                                        className='shrink-0'
                                        // The row is a button; without this the
                                        // picker's own click would also open
                                        // the repeat underneath it.
                                        onClick={e => {
                                          e.stopPropagation()
                                          e.preventDefault()
                                        }}
                                      >
                                        <AssigneeSelect
                                          variant='avatar'
                                          users={assignableUsers ?? []}
                                          value={repeat.assignee ?? null}
                                          disabled={
                                            assignSavingRepeatKey ===
                                            `${step.stepId}:${repeat.repeatIndex}`
                                          }
                                          onChange={assigneeId =>
                                            onAssignRepeat(step, repeat, assigneeId)
                                          }
                                        />
                                      </span>
                                    ) : repeat.assignee ? (
                                      <span
                                        title={`Assigned to ${repeat.assignee.name}`}
                                        className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-[10px] font-semibold text-white'
                                      >
                                        {assigneeInitials(repeat.assignee.name)}
                                      </span>
                                    ) : (
                                      // The same em-dash placeholder the step
                                      // row uses, so an unassigned performance
                                      // keeps the avatar gutter and every row
                                      // in the rail lines up down one edge.
                                      <span className='w-7 shrink-0 text-center text-[10px] text-slate-300'>
                                        —
                                      </span>
                                    )}
                                  </div>
                                </li>
                              )
                            })}
                          </ul>
                        )
                      })()}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </nav>
    </div>
  )
}
